import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusPill } from '@ui-kit';
import type { StatusTone } from '@ui-kit';
import { useAuditLogDetail } from '../hooks/useAuditLogDetail';
import { useDeleteOneAuditLog } from '../hooks/useBulkDeleteAuditLogs';
import { JsonDiffViewer } from './JsonDiffViewer';
import { useHasPermission } from '../../_shared/hooks/useCurrentUser';
import type { AuditResult, AuditSeverity } from '../types/auditLog';

interface AuditLogDrawerProps {
  logId: number | null;
  onClose: () => void;
  /** #454 UAT 2026-05-14 r2: 상세에서 단건 삭제 성공 시 호출 — list 가 토스트 + invalidate 처리 */
  onDeleted?: () => void;
}

function resultTone(result: AuditResult): StatusTone {
  if (result === 'SUCCESS') return 'success';
  if (result === 'DENY') return 'warning';
  return 'danger';
}

function severityTone(severity: AuditSeverity): StatusTone {
  if (severity === 'INFO') return 'neutral';
  if (severity === 'NOTICE') return 'info';
  if (severity === 'WARNING') return 'warning';
  if (severity === 'ALERT') return 'warning';
  return 'danger';
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'Asia/Seoul',
  });
}

/** Drawer 내부 skeleton */
function DrawerSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
      {[1, 2, 3].map((n) => (
        <div
          key={n}
          style={{
            background: 'var(--color-surface-background)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-4)',
          }}
        >
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              style={{
                height: 12,
                width: `${60 + i * 10}%`,
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-surface-elevated)',
                marginBottom: 'var(--spacing-3)',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Drawer 요약 행 */
function DrawerRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '110px 1fr',
        gap: 'var(--spacing-3)',
        padding: 'var(--spacing-2) 0',
        borderBottom: '1px dashed var(--color-surface-border)',
        fontSize: 'var(--font-size-sm)',
        alignItems: 'center',
      }}
    >
      <span
        style={{
          color: 'var(--color-text-secondary)',
          fontWeight: 500,
          fontSize: 'var(--font-size-xs)',
        }}
      >
        {label}
      </span>
      <span
        style={{
          color: 'var(--color-text-primary)',
          wordBreak: 'break-all',
        }}
      >
        {children}
      </span>
    </div>
  );
}

/**
 * AuditLogDrawer — 감사 로그 상세 우측 슬라이드 drawer
 * role=dialog · aria-modal=true · focus trap · ESC 닫기
 */
export function AuditLogDrawer({ logId, onClose, onDeleted }: AuditLogDrawerProps) {
  const { t } = useTranslation();
  const detailQuery = useAuditLogDetail(logId);
  const closeRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const [traceCopied, setTraceCopied] = useState(false);

  const detail = detailQuery.data;

  // #454 UAT 2026-05-14 r2: 상세 [삭제] (admin 전용) state + mutation
  const canDeleteOne = useHasPermission('AUDIT_DELETE');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const deleteOneMutation = useDeleteOneAuditLog();

  const handleDeleteSubmit = useCallback(() => {
    if (logId === null) return;
    deleteOneMutation.mutate(logId, {
      onSuccess: () => {
        setDeleteConfirmOpen(false);
        onDeleted?.();
      },
      onError: () => {
        setDeleteConfirmOpen(false);
      },
    });
  }, [logId, deleteOneMutation, onDeleted]);

  // ESC 닫기
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // focus trap — 드로어 열릴 때 닫기 버튼에 포커스
  useEffect(() => {
    if (logId !== null) {
      setTimeout(() => closeRef.current?.focus(), 50);
    }
  }, [logId]);

  // focus trap — Tab 이 드로어 밖으로 나가지 못하도록
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Tab' || !drawerRef.current) return;
      const focusables = drawerRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    []
  );

  const handleCopyTrace = useCallback(async () => {
    if (!detail?.traceId) return;
    try {
      await navigator.clipboard.writeText(detail.traceId);
      setTraceCopied(true);
      setTimeout(() => setTraceCopied(false), 2000);
    } catch {
      // clipboard API not available
    }
  }, [detail?.traceId]);

  if (logId === null) return null;

  return (
    <>
      {/* 배경 오버레이 */}
      <div
        aria-hidden="true"
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'var(--color-overlay-backdrop)',
          zIndex: 200,
        }}
      />

      {/* 드로어 패널 */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('auditLog.detail.title')}
        aria-describedby="drawer-detail-desc"
        onKeyDown={handleKeyDown}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 'min(540px, 90vw)',
          background: 'var(--color-surface-muted)',
          borderLeft: '1px solid var(--color-surface-border)',
          boxShadow: 'var(--shadow-lg)',
          zIndex: 201,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* 드로어 헤더 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--spacing-3)',
            padding: 'var(--spacing-4) var(--spacing-6)',
            background: 'var(--color-surface-elevated)',
            borderBottom: '1px solid var(--color-surface-border)',
            flexShrink: 0,
          }}
        >
          <h2
            style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 700,
              color: 'var(--color-text-primary)',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
            }}
          >
            {t('auditLog.detail.title')}
            {detail && (
              <span
                style={{
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-brand-primary)',
                  background: 'color-mix(in srgb, var(--color-brand-primary) 12%, transparent)',
                  padding: '1px var(--spacing-2)',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                #{detail.id}
              </span>
            )}
          </h2>
          <button
            ref={closeRef}
            type="button"
            aria-label={t('auditLog.detail.button.close')}
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--color-text-secondary)',
              fontSize: 'var(--font-size-xl)',
              cursor: 'pointer',
              width: 32,
              height: 32,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 'var(--radius-md)',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'var(--color-surface-muted)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
            }}
          >
            ✕
          </button>
        </div>

        {/* 드로어 본문 */}
        <div
          id="drawer-detail-desc"
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 'var(--spacing-4) var(--spacing-6)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-4)',
          }}
        >
          {/* Loading skeleton */}
          {detailQuery.isLoading && <DrawerSkeleton />}

          {/* Error */}
          {detailQuery.isError && (
            <div
              role="alert"
              style={{
                padding: 'var(--spacing-4)',
                background: 'color-mix(in srgb, var(--color-danger) 10%, var(--color-surface-muted))',
                border: '1px solid var(--color-danger)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-danger)',
              }}
            >
              {t('auditLog.detail.error.loadFailed')}
              <button
                type="button"
                onClick={() => void detailQuery.refetch()}
                style={{
                  marginLeft: 'var(--spacing-3)',
                  background: 'none',
                  border: '1px solid var(--color-danger)',
                  color: 'var(--color-danger)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '2px var(--spacing-2)',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-xs)',
                }}
              >
                {t('auditLog.detail.button.retry')}
              </button>
            </div>
          )}

          {/* Default — detail loaded */}
          {detail && (
            <>
              {/* §1 요약 섹션 */}
              <section
                aria-labelledby="drawer-section-summary"
                style={{
                  background: 'var(--color-surface-background)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--spacing-4)',
                }}
              >
                <h3
                  id="drawer-section-summary"
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 700,
                    color: 'var(--color-text-primary)',
                    marginBottom: 'var(--spacing-3)',
                    margin: '0 0 var(--spacing-3)',
                  }}
                >
                  {t('auditLog.detail.section.summary')}
                </h3>

                <DrawerRow label={t('auditLog.detail.field.action')}>
                  <span
                    style={{
                      fontFamily: 'var(--font-family-mono)',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 600,
                    }}
                  >
                    {detail.action}
                  </span>
                  {detail.actionLabel && (
                    <span
                      style={{
                        marginLeft: 'var(--spacing-2)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)',
                      }}
                    >
                      ({detail.actionLabel})
                    </span>
                  )}
                </DrawerRow>

                {(detail.resourceType || detail.resourceId) && (
                  <DrawerRow label={t('auditLog.detail.field.resource')}>
                    <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>
                      {detail.resourceType}{detail.resourceId ? ` / ${detail.resourceId}` : ''}
                    </span>
                  </DrawerRow>
                )}

                <DrawerRow label={t('auditLog.detail.field.actor')}>
                  <span>{detail.actor ?? '-'}</span>
                  {detail.actorName && (
                    <span
                      style={{
                        marginLeft: 'var(--spacing-2)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)',
                      }}
                    >
                      ({detail.actorName})
                    </span>
                  )}
                </DrawerRow>

                <DrawerRow label={t('auditLog.detail.field.result')}>
                  <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
                    <StatusPill tone={resultTone(detail.result)}>
                      {t(`auditLog.result.${detail.result}`)}
                    </StatusPill>
                    <span
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)',
                      }}
                    >
                      {t('auditLog.detail.field.severity')}:
                    </span>
                    <StatusPill tone={severityTone(detail.severity)}>
                      {detail.severity}
                    </StatusPill>
                  </div>
                </DrawerRow>

                {detail.ip && (
                  <DrawerRow label={t('auditLog.detail.field.ip')}>
                    <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>
                      {detail.ip}
                    </span>
                  </DrawerRow>
                )}

                {detail.requestUri && (
                  <DrawerRow label={t('auditLog.detail.field.uri')}>
                    <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', wordBreak: 'break-all' }}>
                      {detail.requestMethod && (
                        <span
                          style={{
                            marginRight: 'var(--spacing-2)',
                            color: 'var(--color-brand-primary)',
                            fontWeight: 600,
                          }}
                        >
                          {detail.requestMethod}
                        </span>
                      )}
                      {detail.requestUri}
                    </span>
                  </DrawerRow>
                )}

                {detail.durationMs !== null && (
                  <DrawerRow label={t('auditLog.detail.field.duration')}>
                    <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>
                      {detail.durationMs}ms
                    </span>
                  </DrawerRow>
                )}

                {detail.traceId && (
                  <DrawerRow label={t('auditLog.detail.field.traceId')}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                      <span
                        style={{
                          fontFamily: 'var(--font-family-mono)',
                          fontSize: 'var(--font-size-xs)',
                          wordBreak: 'break-all',
                        }}
                      >
                        {detail.traceId}
                      </span>
                      <button
                        type="button"
                        aria-label={t('auditLog.detail.button.copyTrace')}
                        onClick={handleCopyTrace}
                        style={{
                          background: 'transparent',
                          border: '1px solid var(--color-surface-border)',
                          color: traceCopied ? 'var(--color-success)' : 'var(--color-text-secondary)',
                          fontSize: '10px',
                          padding: '1px var(--spacing-2)',
                          borderRadius: 'var(--radius-sm)',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          transition: 'color 0.15s',
                        }}
                      >
                        {traceCopied ? t('auditLog.detail.button.copied') : t('auditLog.detail.button.copyTrace')}
                      </button>
                    </span>
                  </DrawerRow>
                )}

                <DrawerRow label={t('auditLog.detail.field.occurredAt')}>
                  <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>
                    {formatDateTime(detail.occurredAt)} KST
                  </span>
                </DrawerRow>
              </section>

              {/* §2 변경 내역 섹션 */}
              {detail.diff && detail.diff.length > 0 && (
                <section
                  aria-labelledby="drawer-section-diff"
                  style={{
                    background: 'var(--color-surface-background)',
                    border: '1px solid var(--color-surface-border)',
                    borderRadius: 'var(--radius-md)',
                    padding: 'var(--spacing-4)',
                  }}
                >
                  <h3
                    id="drawer-section-diff"
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 700,
                      color: 'var(--color-text-primary)',
                      margin: '0 0 var(--spacing-3)',
                    }}
                  >
                    {t('auditLog.detail.section.diff')}
                  </h3>
                  <JsonDiffViewer
                    diff={detail.diff}
                    before={detail.before}
                    after={detail.after}
                  />
                </section>
              )}

              {/* §3 전체 JSON 섹션 (diff 없는 경우에도 before/after 표시) */}
              {(detail.before !== null || detail.after !== null) && (!detail.diff || detail.diff.length === 0) && (
                <section
                  aria-labelledby="drawer-section-json"
                  style={{
                    background: 'var(--color-surface-background)',
                    border: '1px solid var(--color-surface-border)',
                    borderRadius: 'var(--radius-md)',
                    padding: 'var(--spacing-4)',
                  }}
                >
                  <h3
                    id="drawer-section-json"
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 700,
                      color: 'var(--color-text-primary)',
                      margin: '0 0 var(--spacing-3)',
                    }}
                  >
                    {t('auditLog.detail.section.json')}
                  </h3>
                  <JsonDiffViewer
                    diff={[]}
                    before={detail.before}
                    after={detail.after}
                  />
                </section>
              )}

              {/* 기술 정보 (userAgent, payloadHash) */}
              {(detail.userAgent || detail.payloadHash) && (
                <section
                  aria-labelledby="drawer-section-tech"
                  style={{
                    background: 'var(--color-surface-background)',
                    border: '1px solid var(--color-surface-border)',
                    borderRadius: 'var(--radius-md)',
                    padding: 'var(--spacing-4)',
                  }}
                >
                  <h3
                    id="drawer-section-tech"
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 700,
                      color: 'var(--color-text-primary)',
                      margin: '0 0 var(--spacing-3)',
                    }}
                  >
                    {t('auditLog.detail.section.tech')}
                  </h3>
                  {detail.userAgent && (
                    <DrawerRow label="User-Agent">
                      <span
                        style={{
                          fontFamily: 'var(--font-family-mono)',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-secondary)',
                          wordBreak: 'break-all',
                        }}
                      >
                        {detail.userAgent}
                      </span>
                    </DrawerRow>
                  )}
                  {detail.payloadHash && (
                    <DrawerRow label="Payload Hash">
                      <span
                        style={{
                          fontFamily: 'var(--font-family-mono)',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-muted)',
                          wordBreak: 'break-all',
                        }}
                      >
                        {detail.payloadHash}
                      </span>
                    </DrawerRow>
                  )}
                </section>
              )}
            </>
          )}
        </div>

        {/* 드로어 푸터 */}
        <div
          style={{
            padding: 'var(--spacing-3) var(--spacing-6)',
            borderTop: '1px solid var(--color-surface-border)',
            background: 'var(--color-surface-elevated)',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 'var(--spacing-2)',
            flexShrink: 0,
          }}
        >
          {/* #454: admin 전용 [삭제] 버튼 — detail 이 정상 로드된 경우에만 노출 */}
          <div>
            {canDeleteOne && detail && (
              <button
                type="button"
                onClick={() => setDeleteConfirmOpen(true)}
                disabled={deleteOneMutation.isPending}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-danger)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: deleteOneMutation.isPending ? 'not-allowed' : 'pointer',
                  fontFamily: 'var(--font-family-sans)',
                  opacity: deleteOneMutation.isPending ? 0.6 : 1,
                }}
              >
                {t('auditLog.detail.button.delete')}
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('auditLog.detail.button.close')}
          </button>
        </div>
      </div>

      {/* #454: 상세 단건 삭제 confirm 다이얼로그 */}
      {deleteConfirmOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="detail-delete-title"
          style={{
            position: 'fixed', inset: 0, zIndex: 2100,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.55)',
          }}
        >
          <div
            style={{
              background: 'var(--color-surface-background)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--spacing-6)',
              width: 420,
              maxWidth: '90vw',
              boxShadow: 'var(--shadow-xl)',
            }}
          >
            <h2
              id="detail-delete-title"
              style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, marginBottom: 'var(--spacing-3)' }}
            >
              {t('auditLog.detail.confirm.delete.title')}
            </h2>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-5)' }}>
              {t('auditLog.detail.confirm.delete.body')}
            </p>
            <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setDeleteConfirmOpen(false)}
                disabled={deleteOneMutation.isPending}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  cursor: 'pointer',
                }}
              >
                {t('auditLog.detail.confirm.delete.cancel')}
              </button>
              <button
                type="button"
                onClick={handleDeleteSubmit}
                disabled={deleteOneMutation.isPending}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-danger)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: deleteOneMutation.isPending ? 'not-allowed' : 'pointer',
                  opacity: deleteOneMutation.isPending ? 0.6 : 1,
                }}
              >
                {deleteOneMutation.isPending ? '...' : t('auditLog.detail.confirm.delete.submit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
